/**
 * Input — Single-line text input for forms with optional enhancers and validation.
 *
 * Input Component Guidelines
 * - Use `size="sm"` (24px) in compact areas like table cells, toolbars, or inline
 *   editing; `size="base"` (32px) for standard forms and dialogs.
 * - Use `startEnhancer` for contextual hints (search icon, currency symbol) and
 *   `endEnhancer` for interactive actions (clear button, visibility toggle).
 * - Always wrap in `<Field>` + `<FieldLabel>` when used in forms — Input alone has
 *   no accessible label. For search inputs or toolbars where a visible label isn't
 *   needed, add a `<FieldLabel className="sr-only">` or use `aria-label` directly.
 * - Set `invalid` when the field has a validation error — it switches to red border
 *   and background. Pair with a sibling `<FieldError>` to show the message.
 * - Use Input for single-line values. For multi-line text, use Textarea instead.
 * @see https://base-ui.com/react/components/input Base UI Input documentation
 * @see https://ui.shadcn.com/docs/components/input shadcn/ui Input documentation
 */
import type { LayoutClassName } from "@replohq/lattice/types/layoutClassName";
import type { VariantProps } from "class-variance-authority";

import * as React from "react";

import { Input as InputPrimitive } from "@base-ui/react/input";
import { cn } from "@replohq/lattice/utils/cn";
import { cva } from "class-variance-authority";

const inputWrapperVariants = cva(
  "group flex items-center border border-component-border border-0.5 focus-within:border-default aria-[invalid=true]:border-rose-border aria-[invalid=true]:bg-rose-soft focus-within:shadow-2xs transition-all duration-150 w-full cursor-text has-[:disabled]:pointer-events-none has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50",
  {
    variants: {
      size: {
        sm: "h-6 p-1.5 gap-1.5 text-xs rounded-md",
        base: "h-8 px-2 py-1.5 gap-2 text-sm rounded-lg",
      },
      tone: {
        default: "bg-sheet",
        surface: "bg-surface",
      },
    },
    defaultVariants: {
      size: "base",
      tone: "default",
    },
  },
);

export interface InputProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"input">, "className" | "size">,
    VariantProps<typeof inputWrapperVariants> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
  startEnhancer?: React.ReactNode;
  endEnhancer?: React.ReactNode;
  invalid?: boolean;
  wrapperRef?: React.Ref<HTMLDivElement>;
}

function InputInner<ClassName extends string>(
  {
    className,
    UNSAFE_className,
    type,
    size = "base",
    tone = "default",
    startEnhancer,
    endEnhancer,
    invalid,
    wrapperRef,
    ...props
  }: InputProps<ClassName>,
  ref: React.ForwardedRef<HTMLInputElement>,
) {
  const internalRef = React.useRef<HTMLInputElement>(null);
  const inputRef = ref ?? internalRef;

  return (
    <div
      ref={wrapperRef}
      data-slot="input"
      aria-invalid={invalid}
      className={cn(
        inputWrapperVariants({ size, tone }),
        className,
        UNSAFE_className,
      )}
      onClick={() => {
        if (typeof inputRef === "object" && inputRef?.current) {
          inputRef.current.focus();
        }
      }}
    >
      {startEnhancer && (
        <span
          className={cn(
            "text-placeholder group-focus-within:text-default group-aria-[invalid=true]:text-rose flex shrink-0 items-center transition-all duration-150",
            size === "sm" ? "[&_svg]:size-3" : "[&_svg]:size-3.5",
          )}
        >
          {startEnhancer}
        </span>
      )}
      <InputPrimitive
        ref={inputRef}
        type={type}
        className="text-default placeholder:text-placeholder min-w-0 flex-1 bg-transparent outline-none focus:outline-none focus:ring-0"
        {...props}
      />
      {endEnhancer && (
        <span
          className={cn(
            "text-placeholder group-focus-within:text-default group-aria-[invalid=true]:text-rose flex shrink-0 items-center transition-all duration-150",
            size === "sm" ? "[&_svg]:size-3" : "[&_svg]:size-3.5",
          )}
        >
          {endEnhancer}
        </span>
      )}
    </div>
  );
}

// NOTE (Ben, 01/13/2026): Type cast to preserve generic type parameter for className prop
// See: https://fettblog.eu/typescript-react-generic-forward-refs/
// Legacy cast - may or may not be necessary
// eslint-disable-next-line replo/no-as-cast
const Input = React.forwardRef(InputInner) as <ClassName extends string>(
  props: InputProps<ClassName> & React.RefAttributes<HTMLInputElement>,
) => React.ReactNode;

export { Input };
